import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowLeft,
  Ban,
  BarChart3,
  Box,
  CheckCircle2,
  Clock3,
  Crown,
  Flag,
  LifeBuoy,
  MessageSquareReply,
  MoreHorizontal,
  PieChart,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Search,
  Send,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  TrendingUp,
  UserCog,
  Users,
  XCircle,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import SupportQueue from "../components/SupportQueue";
import {
  BlackboxAdminStatus,
  BlackboxAdminTransmission,
  BlackboxCaseStatus,
  listBlackboxTransmissions,
  publishBlackboxTransmission,
  replyFromSentinel,
  updateBlackboxTransmissionStatus,
} from "../api/blackbox";
import { formatBlackspaceDateTime } from "../utils/time";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

type SentinelTab = "pulse" | "metrics" | "blackbox" | "support" | "realm" | "orders" | "reports" | "permissions" | "underground";

type Realm = {
  role: string;
  isPresence: boolean;
  permissions: string[];
  canAccessSentinel: boolean;
};

type Analytics = {
  totals: {
    users: number;
    posts: number;
    spaces: number;
    conversations: number;
    messageMetadata: number;
    blackbox: number;
  };
  sevenDays: {
    users: number;
    posts: number;
    spaces: number;
    conversations: number;
    messageMetadata: number;
    blackbox: number;
    reports: number;
  };
  previousSevenDays: {
    users: number;
    posts: number;
    spaces: number;
    conversations: number;
    messageMetadata: number;
    blackbox: number;
    reports: number;
  };
  changes: {
    users: number;
    posts: number;
    spaces: number;
    conversations: number;
    messageMetadata: number;
    blackbox: number;
    reports: number;
  };
  momentum: { label: string; value: number }[];
  blackboxByStatus: { status: string; count: number }[];
  reportsByStatus: { status: string; count: number }[];
  reportsBySeverity: { severity: string; count: number }[];
  metrics: {
    trends: { date: string; users: number; posts: number; messages: number; blackbox: number; reports: number }[];
    blackboxFunnel: { status: string; count: number }[];
    topSpaces: { id: string; title: string; posts: number; reports: number; members: number }[];
    activityRhythm: { day: number; hour: number; count: number }[];
  };
};

type RealmUser = {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string;
  createdAt: string;
  lastActiveAt?: string | null;
  realmStatus: "active" | "restricted" | "banned";
  realmReason: string;
  protected: boolean;
  role: "user" | "mod" | "admin" | "patron" | "presence";
  permissions: string[];
  grantExpiresAt?: string | null;
  grantProtected?: boolean;
  grantLocked?: boolean;
  grantLockedReason?: string;
  isPresence?: boolean;
  publicActivity: {
    posts: number;
    messagesSent: number;
    spacesJoined: number;
  };
};

type GrantOptions = {
  reason?: string;
  permissions?: string[];
  expiresAt?: string | null;
  protected?: boolean;
};

type RealmOrder = {
  id: string;
  actorUsername: string;
  actorRole: string;
  targetUsername: string;
  action: string;
  subject: string;
  details: Record<string, any>;
  createdAt: string;
};

type RealmReport = {
  id: string;
  spaceId: string;
  spaceTitle: string;
  reporterUsername: string;
  targetType: string;
  targetId: string;
  reason: string;
  details: string;
  status: "open" | "in_review" | "resolved" | "dismissed" | "escalated";
  severity: "low" | "medium" | "high";
  handledByRole?: string;
  handledAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

type ReportsSummary = {
  total: number;
  byStatus: { status: string; count: number }[];
  bySeverity: { severity: string; count: number }[];
};

const statusOptions: { value: BlackboxAdminStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "received", label: "Received" },
  { value: "in_review", label: "In review" },
  { value: "reply_requested", label: "Reply requested" },
  { value: "reply_back", label: "Reply back" },
  { value: "rejected", label: "Rejected" },
  { value: "posted", label: "Posted" },
  { value: "closed", label: "Closed" },
];

const tabs: { id: SentinelTab; label: string; icon: React.ElementType; permission?: string; presenceOnly?: boolean }[] = [
  { id: "pulse", label: "Pulse", icon: BarChart3, permission: "analytics:read" },
  { id: "metrics", label: "Metrics", icon: Activity, permission: "analytics:read" },
  { id: "blackbox", label: "Blackbox", icon: Box, permission: "blackbox:read" },
  { id: "support", label: "Support", icon: LifeBuoy, permission: "support:read" },
  { id: "realm", label: "Members", icon: Users, permission: "users:read" },
  { id: "orders", label: "Action log", icon: ScrollText, permission: "orders:read" },
  { id: "reports", label: "Reports", icon: Flag, permission: "reports:read" },
  { id: "permissions", label: "Access", icon: Crown, permission: "permissions:write" },
  { id: "underground", label: "Restricted", icon: Ban, permission: "users:write" },
];

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || `Request failed: ${res.status}`);
  return data as T;
}

function hasPermission(realm: Realm | null, permission?: string) {
  if (!realm) return false;
  if (!permission) return true;
  return realm.permissions?.includes("*") || realm.permissions?.includes(permission);
}

function formatDate(value?: string | null) {
  if (!value) return "Not yet";
  return formatBlackspaceDateTime(value, "Unknown");
}

function statusTone(status: BlackboxCaseStatus) {
  if (status === "posted") return "border-emerald-400/25 bg-emerald-400/10 text-emerald-200";
  if (status === "reply_requested") return "border-cyan-300/25 bg-cyan-300/10 text-cyan-100";
  if (status === "reply_back") return "border-fuchsia-300/25 bg-fuchsia-300/10 text-fuchsia-100";
  if (status === "in_review") return "border-amber-300/25 bg-amber-300/10 text-amber-100";
  if (status === "rejected") return "border-red-300/25 bg-red-400/10 text-red-100";
  if (status === "closed") return "border-neutral-500/25 bg-neutral-700/30 text-neutral-300";
  return "border-fuchsia-300/25 bg-fuchsia-300/10 text-fuchsia-100";
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    received: "Received",
    in_review: "In review",
    reply_requested: "Reply requested",
    reply_back: "Reply back",
    rejected: "Rejected",
    posted: "Posted",
    closed: "Closed",
  };
  return labels[status] || status;
}

function reportStatusLabel(status: string) {
  const labels: Record<string, string> = {
    open: "Open",
    in_review: "In review",
    resolved: "Resolved",
    dismissed: "Dismissed",
    escalated: "Escalated",
  };
  return labels[status] || status;
}

function reportStatusTone(status: string) {
  if (status === "resolved") return "border-emerald-300/25 bg-emerald-300/10 text-emerald-100";
  if (status === "dismissed") return "border-neutral-600 bg-neutral-800/50 text-neutral-300";
  if (status === "escalated") return "border-fuchsia-300/25 bg-fuchsia-300/10 text-fuchsia-100";
  if (status === "in_review") return "border-cyan-300/25 bg-cyan-300/10 text-cyan-100";
  return "border-amber-300/25 bg-amber-300/10 text-amber-100";
}

function formatChange(value?: number) {
  const safe = Number.isFinite(value || 0) ? Number(value || 0) : 0;
  if (safe === 0) return "0%";
  return `${safe > 0 ? "+" : ""}${safe.toFixed(Math.abs(safe) >= 10 ? 0 : 1)}%`;
}

function changeTone(value?: number, accent: "good" | "signal" = "good") {
  const safe = Number(value || 0);
  if (safe > 0) {
    return accent === "signal"
      ? "border-fuchsia-300/25 bg-fuchsia-300/10 text-fuchsia-100"
      : "border-emerald-300/25 bg-emerald-300/10 text-emerald-100";
  }
  if (safe < 0) return "border-amber-300/25 bg-amber-300/10 text-amber-100";
  return "border-neutral-700 bg-neutral-900 text-neutral-300";
}

function compactNumber(value: number) {
  return new Intl.NumberFormat(undefined, { notation: value >= 10000 ? "compact" : "standard" }).format(value || 0);
}

function roleOptionsFor(realm: Realm | null): RealmUser["role"][] {
  if (realm?.isPresence) return ["user", "mod", "admin", "patron"];
  if (hasPermission(realm, "permissions:write")) return ["user", "admin"];
  return ["user"];
}

function roleLabel(role: string) {
  if (role === "presence") return "Owner";
  if (role === "patron") return "Owner";
  if (role === "mod") return "Moderator";
  if (role === "admin") return "Admin";
  if (role === "delegate") return "Admin";
  if (role === "creator") return "Owner";
  if (role === "co_creator") return "Owner";
  if (role === "space_admin") return "Admin";
  return "Member";
}

const grantPowerOptions = [
  { value: "analytics:read", label: "Pulse" },
  { value: "reports:read", label: "Read reports" },
  { value: "reports:write", label: "Update reports" },
  { value: "posts:remove", label: "Remove posts" },
  { value: "reports:review", label: "Review space reports" },
  { value: "reports:escalate", label: "Escalate reports" },
  { value: "roles:space_admin:write", label: "Manage Admins" },
  { value: "users:space_mute", label: "Mute in spaces" },
  { value: "users:space_ban", label: "Ban in spaces" },
  { value: "blackbox:read", label: "Read Blackbox" },
  { value: "blackbox:reply", label: "Reply Blackbox" },
  { value: "blackbox:status", label: "Change Blackbox" },
  { value: "support:read", label: "Read support" },
  { value: "support:reply", label: "Reply as Blackspace" },
  { value: "support:status", label: "Manage support" },
  { value: "users:read", label: "Read users" },
  { value: "users:write", label: "Govern users" },
  { value: "orders:read", label: "Read orders" },
  { value: "orders:write", label: "Record orders" },
];

function currentTokenUserId() {
  try {
    const token = localStorage.getItem("token");
    const payload = token?.split(".")[1];
    if (!payload) return "";
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded))?.id || "";
  } catch {
    return "";
  }
}

export default function Sentinel() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as { from?: string; reopenBlackspaceMenuOnBack?: boolean } | null;
  const originRef = useRef<string | null>(routeState?.from || null);
  const reopenMenuOnBackRef = useRef(Boolean(routeState?.reopenBlackspaceMenuOnBack));
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [realm, setRealm] = useState<Realm | null>(null);
  const [activeTab, setActiveTab] = useState<SentinelTab>("pulse");
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [refreshingRealm, setRefreshingRealm] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [caseStatus, setCaseStatus] = useState<BlackboxAdminStatus>("all");
  const [caseQuery, setCaseQuery] = useState("");
  const [cases, setCases] = useState<BlackboxAdminTransmission[]>([]);
  const [caseTotal, setCaseTotal] = useState(0);
  const [casesLoading, setCasesLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [reopenMenu, setReopenMenu] = useState<string | null>(null);

  const [usersQuery, setUsersQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [realmUsers, setRealmUsers] = useState<RealmUser[]>([]);
  const [accessQuery, setAccessQuery] = useState("");
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessUsers, setAccessUsers] = useState<RealmUser[]>([]);
  const [undergroundQuery, setUndergroundQuery] = useState("");
  const [undergroundStatus, setUndergroundStatus] = useState<"all" | "restricted" | "banned">("all");
  const [undergroundLoading, setUndergroundLoading] = useState(false);
  const [undergroundUsers, setUndergroundUsers] = useState<RealmUser[]>([]);
  const [statusReasons, setStatusReasons] = useState<Record<string, string>>({});
  const [roleReasons, setRoleReasons] = useState<Record<string, string>>({});
  const [rowToasts, setRowToasts] = useState<Record<string, string>>({});

  const [orders, setOrders] = useState<RealmOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [interventionNote, setInterventionNote] = useState("");
  const [reports, setReports] = useState<RealmReport[]>([]);
  const [reportsSummary, setReportsSummary] = useState<ReportsSummary | null>(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportStatus, setReportStatus] = useState("all");
  const [reportQuery, setReportQuery] = useState("");

  const currentUserId = useMemo(() => currentTokenUserId(), []);

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (tab.presenceOnly) return Boolean(realm?.isPresence);
      return hasPermission(realm, tab.permission);
    });
  }, [realm]);

  useEffect(() => {
    if (realm && visibleTabs.length > 0 && !visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [realm, visibleTabs, activeTab]);

  const mobileActionTabs = useMemo(
    () => visibleTabs.filter((tab) => ["realm", "permissions", "underground", "blackbox", "support", "orders", "reports"].includes(tab.id)),
    [visibleTabs]
  );

  useEffect(() => {
    if (!realm || typeof window === "undefined" || window.innerWidth > 767) return;
    if (activeTab !== "pulse" && activeTab !== "metrics") return;
    const preferred = mobileActionTabs[0]?.id;
    if (preferred) setActiveTab(preferred);
  }, [activeTab, mobileActionTabs, realm]);

  const caseCounts = useMemo(() => {
    const current = cases.length;
    return caseTotal > current ? `${current} of ${caseTotal}` : String(caseTotal);
  }, [cases.length, caseTotal]);

  function flash(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 3200);
  }

  const mobileCommandTarget = useMemo<SentinelTab>(() => {
    if (["realm", "permissions", "underground", "blackbox", "orders", "reports"].includes(activeTab)) return activeTab;
    return (mobileActionTabs[0]?.id as SentinelTab) || activeTab;
  }, [activeTab, mobileActionTabs]);

  const mobileCommandValue =
    mobileCommandTarget === "realm" ? usersQuery :
    mobileCommandTarget === "permissions" ? accessQuery :
    mobileCommandTarget === "underground" ? undergroundQuery :
    mobileCommandTarget === "blackbox" ? caseQuery :
    mobileCommandTarget === "reports" ? reportQuery :
    "";

  const mobileCommandPlaceholder =
    mobileCommandTarget === "permissions" ? "Search access..." :
    mobileCommandTarget === "underground" ? "Search banned or restricted..." :
    mobileCommandTarget === "blackbox" ? "Search Blackbox cases..." :
    mobileCommandTarget === "reports" ? "Search reports..." :
    mobileCommandTarget === "orders" ? "Orders do not need search." :
    "Search members...";

  const activeTabMeta = visibleTabs.find((tab) => tab.id === activeTab) || visibleTabs[0];
  const activeTabLabel = activeTabMeta?.label || "Sentinel";
  const hasActiveSearch = Boolean(String(mobileCommandValue || "").trim());
  const hasActiveFilter =
    (activeTab === "blackbox" && caseStatus !== "all") ||
    (activeTab === "reports" && reportStatus !== "all") ||
    (activeTab === "underground" && undergroundStatus !== "all");

  function clearHeaderSearch() {
    setMobileCommandValue("");
    if (mobileCommandTarget === "permissions") loadAccessUsers("");
    else if (mobileCommandTarget === "underground") loadUndergroundUsers("", undergroundStatus);
    else if (mobileCommandTarget === "blackbox") loadCases(caseStatus, "");
    else if (mobileCommandTarget === "reports") loadReports(reportStatus, "");
    else if (mobileCommandTarget === "realm") loadUsers("");
  }

  function setMobileCommandValue(value: string) {
    if (mobileCommandTarget === "permissions") setAccessQuery(value);
    else if (mobileCommandTarget === "underground") setUndergroundQuery(value);
    else if (mobileCommandTarget === "blackbox") setCaseQuery(value);
    else if (mobileCommandTarget === "reports") setReportQuery(value);
    else if (mobileCommandTarget === "orders") return;
    else setUsersQuery(value);
  }

  function runMobileCommandSearch() {
    if (activeTab !== mobileCommandTarget) setActiveTab(mobileCommandTarget);
    if (mobileCommandTarget === "permissions") loadAccessUsers(accessQuery);
    else if (mobileCommandTarget === "underground") loadUndergroundUsers(undergroundQuery, undergroundStatus);
    else if (mobileCommandTarget === "blackbox") loadCases(caseStatus, caseQuery);
    else if (mobileCommandTarget === "orders") loadOrders();
    else if (mobileCommandTarget === "reports") loadReports(reportStatus, reportQuery);
    else if (mobileCommandTarget === "realm") loadUsers();
  }

  function flashRow(userId: string, message: string) {
    setRowToasts((current) => ({ ...current, [userId]: message }));
    window.setTimeout(() => {
      setRowToasts((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }, 3200);
  }

  async function verifyAccess() {
    try {
      const data = await api<{ success: boolean; realm: Realm }>("/api/sentinel/me");
      if (!data.realm?.canAccessSentinel) {
        throw new Error("Sentinel unavailable");
      }
      setRealm(data.realm);
      setAllowed(true);
      const firstTab = tabs.find((tab) => (tab.presenceOnly ? data.realm.isPresence : hasPermission(data.realm, tab.permission)));
      setActiveTab(firstTab?.id || "blackbox");
    } catch {
      setAllowed(false);
    }
  }

  async function loadAnalytics() {
    if (!hasPermission(realm, "analytics:read")) return;
    setAnalyticsLoading(true);
    try {
      const data = await api<{ success: boolean; analytics: Analytics }>("/api/sentinel/analytics");
      setAnalytics(data.analytics);
    } catch (err: any) {
      setError(err.message || "Could not load Sentinel pulse.");
    } finally {
      setAnalyticsLoading(false);
    }
  }

  async function loadCases(nextStatus = caseStatus, nextQuery = caseQuery) {
    if (!hasPermission(realm, "blackbox:read")) return;
    setCasesLoading(true);
    setError("");
    try {
      const data = await listBlackboxTransmissions({
        status: nextStatus,
        q: nextQuery,
        page: 1,
        limit: 30,
      });
      setCases(data.transmissions || []);
      setCaseTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message || "Could not load Blackbox cases.");
    } finally {
      setCasesLoading(false);
    }
  }

  async function loadUsers(nextQuery = usersQuery) {
    if (!hasPermission(realm, "users:read")) return;
    setUsersLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({ q: nextQuery, page: "1", limit: "16", scope: "roles" });
      const data = await api<{ success: boolean; users: RealmUser[] }>(`/api/sentinel/users?${q.toString()}`);
      setRealmUsers(data.users || []);
    } catch (err: any) {
      setError(err.message || "Could not load realm users.");
    } finally {
      setUsersLoading(false);
    }
  }

  async function loadAccessUsers(nextQuery = accessQuery) {
    if (!hasPermission(realm, "permissions:write")) return;
    setAccessLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({ q: nextQuery, page: "1", limit: "18", scope: "access" });
      const data = await api<{ success: boolean; users: RealmUser[] }>(`/api/sentinel/users?${q.toString()}`);
      setAccessUsers(data.users || []);
    } catch (err: any) {
      setError(err.message || "Could not load access users.");
    } finally {
      setAccessLoading(false);
    }
  }

  async function loadUndergroundUsers(nextQuery = undergroundQuery, nextStatus = undergroundStatus) {
    if (!hasPermission(realm, "users:write")) return;
    setUndergroundLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({
        q: nextQuery,
        status: nextStatus,
        page: "1",
        limit: "24",
        scope: "underground",
      });
      const data = await api<{ success: boolean; users: RealmUser[] }>(`/api/sentinel/users?${q.toString()}`);
      setUndergroundUsers(data.users || []);
    } catch (err: any) {
      setError(err.message || "Could not load restricted users.");
    } finally {
      setUndergroundLoading(false);
    }
  }

  async function loadOrders() {
    if (!hasPermission(realm, "orders:read")) return;
    setOrdersLoading(true);
    setError("");
    try {
      const data = await api<{ success: boolean; orders: RealmOrder[] }>("/api/sentinel/orders");
      setOrders(data.orders || []);
    } catch (err: any) {
      setError(err.message || "Could not load orders.");
    } finally {
      setOrdersLoading(false);
    }
  }

  async function loadReports(nextStatus = reportStatus, nextQuery = reportQuery) {
    if (!hasPermission(realm, "reports:read")) return;
    setReportsLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({
        status: nextStatus,
        q: nextQuery,
        limit: "100",
      });
      const data = await api<{ success: boolean; reports: RealmReport[]; summary: ReportsSummary }>(`/api/sentinel/reports?${q.toString()}`);
      setReports(data.reports || []);
      setReportsSummary(data.summary || null);
    } catch (err: any) {
      setError(err.message || "Could not load reports.");
    } finally {
      setReportsLoading(false);
    }
  }

  async function refreshRealm() {
    if (!realm || refreshingRealm) return;
    setRefreshingRealm(true);
    setError("");
    try {
      const data = await api<{ success: boolean; realm: Realm }>("/api/sentinel/me");
      setRealm(data.realm);
      await Promise.all([
        hasPermission(data.realm, "analytics:read") ? loadAnalytics() : Promise.resolve(),
        hasPermission(data.realm, "blackbox:read") ? loadCases(caseStatus, caseQuery) : Promise.resolve(),
        hasPermission(data.realm, "users:read") ? loadUsers(usersQuery) : Promise.resolve(),
        hasPermission(data.realm, "permissions:write") ? loadAccessUsers(accessQuery) : Promise.resolve(),
        hasPermission(data.realm, "users:write") ? loadUndergroundUsers(undergroundQuery, undergroundStatus) : Promise.resolve(),
        hasPermission(data.realm, "orders:read") ? loadOrders() : Promise.resolve(),
        hasPermission(data.realm, "reports:read") ? loadReports(reportStatus, reportQuery) : Promise.resolve(),
      ]);
      flash("Review tools refreshed.");
    } catch (err: any) {
      setError(err.message || "Refresh failed.");
    } finally {
      setRefreshingRealm(false);
    }
  }

  useEffect(() => {
    verifyAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!realm) return;
    if (hasPermission(realm, "analytics:read")) loadAnalytics();
    if (hasPermission(realm, "blackbox:read")) loadCases(caseStatus, caseQuery);
    if (hasPermission(realm, "users:read")) loadUsers();
    if (hasPermission(realm, "permissions:write")) loadAccessUsers();
    if (hasPermission(realm, "users:write")) loadUndergroundUsers();
    if (hasPermission(realm, "orders:read")) loadOrders();
    if (hasPermission(realm, "reports:read")) loadReports(reportStatus, reportQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realm]);

  useEffect(() => {
    if (realm && activeTab === "blackbox") loadCases(caseStatus, caseQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseStatus]);

  useEffect(() => {
    if (realm && activeTab === "reports") loadReports(reportStatus, reportQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportStatus]);

  useEffect(() => {
    if (!realm || activeTab !== "blackbox" || !hasPermission(realm, "blackbox:read")) return;
    const timer = window.setTimeout(() => loadCases(caseStatus, caseQuery), 260);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseQuery, activeTab, realm]);

  useEffect(() => {
    if (!realm || activeTab !== "reports" || !hasPermission(realm, "reports:read")) return;
    const timer = window.setTimeout(() => loadReports(reportStatus, reportQuery), 260);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportQuery, activeTab, realm]);

  useEffect(() => {
    if (!realm || activeTab !== "realm" || !hasPermission(realm, "users:read")) return;
    const timer = window.setTimeout(() => loadUsers(usersQuery), 260);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersQuery, activeTab, realm]);

  useEffect(() => {
    if (!realm || activeTab !== "permissions" || !hasPermission(realm, "permissions:write")) return;
    const timer = window.setTimeout(() => loadAccessUsers(accessQuery), 260);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessQuery, activeTab, realm]);

  useEffect(() => {
    if (!realm || activeTab !== "underground" || !hasPermission(realm, "users:write")) return;
    const timer = window.setTimeout(() => loadUndergroundUsers(undergroundQuery, undergroundStatus), 260);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undergroundQuery, undergroundStatus, activeTab, realm]);

  async function changeCaseStatus(item: BlackboxAdminTransmission, nextStatus: BlackboxCaseStatus) {
    setBusyId(item.id);
    setError("");
    try {
      const data = await updateBlackboxTransmissionStatus(item.id, nextStatus, item.internalNote);
      setCases((current) => current.map((entry) => (entry.id === item.id ? data.transmission : entry)));
      flash(`Blackbox case marked ${statusLabel(nextStatus)}.`);
      setReopenMenu(null);
    } catch (err: any) {
      setError(err.message || "Status update failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function publishCase(item: BlackboxAdminTransmission) {
    setBusyId(item.id);
    setError("");
    try {
      const data = await publishBlackboxTransmission(item.id);
      setCases((current) => current.map((entry) => (entry.id === item.id ? data.transmission : entry)));
      flash("This message is published for the Transmission.");
      loadAnalytics();
    } catch (err: any) {
      setError(err.message || "Publish failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function sendPresenceReply(item: BlackboxAdminTransmission) {
    const reply = (replyDrafts[item.id] || "").trim();
    if (reply.length < 2) {
      setError("Reply needs at least 2 characters.");
      return;
    }
    setBusyId(item.id);
    setError("");
    try {
      const data = await replyFromSentinel(item.id, reply);
      setCases((current) => current.map((entry) => (entry.id === item.id ? data.transmission : entry)));
      setReplyDrafts((current) => ({ ...current, [item.id]: "" }));
      flash("Reply sent to the Blackbox receipt.");
    } catch (err: any) {
      setError(err.message || "Reply failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function updateUserStatus(user: RealmUser, status: RealmUser["realmStatus"]) {
    setError("");
    try {
      if (user.realmStatus === status) {
        flashRow(user.id, status === "active" ? "Already active." : `Already ${status}.`);
        return;
      }
      const reason = (statusReasons[user.id] || user.realmReason || "").trim();
      if (!realm?.isPresence && reason.length < 3) {
        flashRow(user.id, "Reason required.");
        return;
      }
      const data = await api<{ success: boolean; message?: string; user: RealmUser }>(`/api/sentinel/users/${user.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status, reason }),
      });
      setRealmUsers((current) => current.map((entry) => (entry.id === user.id ? data.user : entry)));
      setAccessUsers((current) => current.map((entry) => (entry.id === user.id ? data.user : entry)));
      setUndergroundUsers((current) => current.map((entry) => (entry.id === user.id ? data.user : entry)));
      flashRow(user.id, data.message || `Marked ${status}.`);
      loadUndergroundUsers();
      loadOrders();
    } catch (err: any) {
      flashRow(user.id, err.message || "User status update failed.");
    }
  }

  async function updateUserRole(user: RealmUser, role: RealmUser["role"], options: GrantOptions = {}) {
    setError("");
    try {
      const reason = (options.reason ?? roleReasons[user.id] ?? "").trim();
      if (!realm?.isPresence && reason.length < 3) {
        flashRow(user.id, "Reason required.");
        return;
      }
      const data = await api<{ success: boolean; user: RealmUser }>(`/api/sentinel/users/${user.id}/role`, {
        method: "PATCH",
        body: JSON.stringify({
          role,
          reason,
          permissions: options.permissions,
          expiresAt: options.expiresAt,
          protected: options.protected,
        }),
      });
      setRealmUsers((current) => current.map((entry) => (entry.id === user.id ? data.user : entry)));
      setAccessUsers((current) => {
        const exists = current.some((entry) => entry.id === user.id);
        const next = exists
          ? current.map((entry) => (entry.id === user.id ? data.user : entry))
          : [data.user, ...current];
        return next.filter((entry) => accessQuery.trim() || entry.role !== "user");
      });
      flashRow(user.id, `Role updated to ${roleLabel(role)}.`);
      loadUsers();
      loadOrders();
    } catch (err: any) {
      flashRow(user.id, err.message || "Role update failed.");
    }
  }

  async function updateUserProtection(user: RealmUser, protectedState: boolean) {
    setError("");
    try {
      const data = await api<{ success: boolean; user: RealmUser }>(`/api/sentinel/users/${user.id}/protection`, {
        method: "PATCH",
        body: JSON.stringify({ protected: protectedState, reason: roleReasons[user.id] || "" }),
      });
      setRealmUsers((current) => current.map((entry) => (entry.id === user.id ? data.user : entry)));
      setAccessUsers((current) => current.map((entry) => (entry.id === user.id ? data.user : entry)));
      flashRow(user.id, protectedState ? "User protected." : "Protection removed.");
    } catch (err: any) {
      flashRow(user.id, err.message || "Protection update failed.");
    }
  }

  async function createIntervention() {
    const note = interventionNote.trim();
    if (!note) return;
    setError("");
    try {
      const data = await api<{ success: boolean; message?: string }>("/api/sentinel/orders", {
        method: "POST",
        body: JSON.stringify({ action: "intervention.note", note }),
      });
      setInterventionNote("");
      flash(data.message || "Intervention recorded.");
      loadOrders();
    } catch (err: any) {
      setError(err.message || "Could not record intervention.");
    }
  }

  async function updateReportStatus(report: RealmReport, status: RealmReport["status"]) {
    setError("");
    try {
      const data = await api<{ success: boolean; report: RealmReport }>(`/api/sentinel/reports/${report.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      setReports((current) => current.map((entry) => (entry.id === report.id ? data.report : entry)));
      flash(`Report marked ${reportStatusLabel(status)}.`);
      loadOrders();
    } catch (err: any) {
      setError(err.message || "Report update failed.");
    }
  }

  if (allowed === null) {
    return (
      <div className="min-h-screen bg-black text-neutral-100">
        <main className="mx-auto flex min-h-[calc(100vh-76px)] max-w-5xl items-center justify-center px-4">
          <div className="flex items-center gap-3 text-neutral-400">
            <RefreshCw size={18} className="animate-spin text-cyan-200" />
            Opening the Sentinel...
          </div>
        </main>
        <FooterNav />
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/home" replace />;
  }

  function goBack() {
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (originRef.current && originRef.current !== currentRoute) {
      navigate(originRef.current, { state: reopenMenuOnBackRef.current ? { reopenBlackspaceMenu: true } : undefined });
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/home");
  }

  return (
    <div className="bs-mobile-sentinel flex h-screen flex-col overflow-hidden bg-[radial-gradient(circle_at_18%_-8%,rgba(34,211,238,0.16),transparent_34%),radial-gradient(circle_at_92%_0%,rgba(244,114,182,0.08),transparent_28%),linear-gradient(180deg,#040506_0%,#020202_58%,#000_100%)] text-neutral-100">
      <main className="flex min-h-0 w-full flex-1 px-0 pb-0 pt-0">
        <section className="bs-sentinel-shell relative flex min-h-0 w-full flex-col overflow-hidden bg-transparent">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,0.018)_1px,transparent_1px)] bg-[size:44px_44px] opacity-[0.18]" />
          <div className="relative z-[1400] shrink-0 border-b border-white/[0.06] bg-black/20 px-4 py-4 backdrop-blur-xl sm:px-8">
            <div className="relative flex items-center justify-between gap-3">
              <button type="button" onClick={goBack} className="bs-home-mobile-command md:hidden" aria-label="Back">
                <ArrowLeft size={19} />
              </button>

              <div className="min-w-0 flex-1 px-1">
                <h1 className="bs-message-page-title truncate">Sentinel</h1>
                <p className="mt-1 truncate text-xs font-semibold text-neutral-500">{activeTabLabel}</p>
              </div>

              <div className="flex items-center gap-2">
                {toast && (
                  <div className="hidden max-w-xs items-center gap-2 rounded-xl border border-emerald-300/25 bg-emerald-300/10 px-3 py-2 text-xs font-black text-emerald-50 shadow-2xl shadow-emerald-500/10 backdrop-blur sm:flex">
                    <CheckCircle2 size={15} className="shrink-0 text-emerald-200" />
                    <span className="truncate">{toast}</span>
                  </div>
                )}
                {activeTab !== "support" && <button type="button" onClick={() => setSearchOpen((value) => !value)} className={`bs-home-mobile-command bs-sentinel-command ${searchOpen || hasActiveSearch ? "is-active" : ""}`} aria-label="Search Sentinel" aria-pressed={searchOpen}>
                  <Search size={18} />
                </button>}
                <div className="relative">
                  <button type="button" onClick={() => setFilterOpen((value) => !value)} className={`bs-home-mobile-command bs-sentinel-command ${filterOpen || hasActiveFilter ? "is-active" : ""}`} aria-label="Filter Sentinel" aria-pressed={filterOpen}>
                    <SlidersHorizontal size={18} />
                  </button>
                  {filterOpen && (
                    <div className="absolute right-0 top-14 z-[2600] w-72 rounded-2xl border border-cyan-300/15 bg-black/90 p-3 shadow-[0_24px_70px_rgba(0,0,0,0.5)] backdrop-blur-xl">
                      <p className="px-2 pb-2 text-[11px] font-black uppercase tracking-[0.22em] text-neutral-500">View</p>
                      <div className="grid grid-cols-2 gap-2">
                        {visibleTabs.map((tab) => {
                          const Icon = tab.icon;
                          const active = activeTab === tab.id;
                          return (
                            <button key={`filter-${tab.id}`} type="button" onClick={() => { setActiveTab(tab.id); setFilterOpen(false); }} className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-xs font-black transition ${active ? "bg-cyan-300/12 text-cyan-100 shadow-[0_0_22px_rgba(34,211,238,0.12)]" : "text-neutral-400 hover:bg-white/5 hover:text-white"}`}>
                              <Icon size={15} className={active ? "text-cyan-200" : "text-neutral-500"} />
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>
                      {activeTab === "blackbox" && (
                        <>
                          <p className="px-2 pb-2 pt-4 text-[11px] font-black uppercase tracking-[0.22em] text-neutral-500">Blackbox status</p>
                          <div className="grid grid-cols-2 gap-2">
                            {statusOptions.map((option) => (
                              <button key={option.value} type="button" onClick={() => setCaseStatus(option.value)} className={`rounded-xl px-3 py-2 text-left text-xs font-black transition ${caseStatus === option.value ? "bg-cyan-300/12 text-cyan-100" : "text-neutral-500 hover:bg-white/5 hover:text-white"}`}>
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      {activeTab === "reports" && (
                        <>
                          <p className="px-2 pb-2 pt-4 text-[11px] font-black uppercase tracking-[0.22em] text-neutral-500">Report status</p>
                          <div className="grid grid-cols-2 gap-2">
                            {(["all", "in_review", "escalated", "resolved", "dismissed"] as string[]).map((status) => (
                              <button key={status} type="button" onClick={() => setReportStatus(status)} className={`rounded-xl px-3 py-2 text-left text-xs font-black transition ${reportStatus === status ? "bg-cyan-300/12 text-cyan-100" : "text-neutral-500 hover:bg-white/5 hover:text-white"}`}>
                                {status === "all" ? "All" : reportStatusLabel(status)}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      {activeTab === "underground" && (
                        <>
                          <p className="px-2 pb-2 pt-4 text-[11px] font-black uppercase tracking-[0.22em] text-neutral-500">User status</p>
                          <div className="grid grid-cols-3 gap-2">
                            {(["all", "restricted", "banned"] as const).map((status) => (
                              <button key={status} type="button" onClick={() => setUndergroundStatus(status)} className={`rounded-xl px-3 py-2 text-left text-xs font-black capitalize transition ${undergroundStatus === status ? "bg-cyan-300/12 text-cyan-100" : "text-neutral-500 hover:bg-white/5 hover:text-white"}`}>
                                {status}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button type="button" onClick={refreshRealm} disabled={refreshingRealm} className="bs-home-mobile-command bs-sentinel-command disabled:cursor-wait disabled:opacity-60" aria-label="Refresh Sentinel" title="Refresh Sentinel">
                  <RefreshCw size={18} className={refreshingRealm ? "animate-spin text-cyan-300" : ""} />
                </button>
              </div>
            </div>

            {searchOpen && activeTab !== "support" && (
              <form
                className="relative mt-4 flex items-center gap-2 rounded-2xl bg-black/70 px-4 py-3 ring-1 ring-cyan-300/15 focus-within:ring-cyan-300/45"
                onSubmit={(event) => {
                  event.preventDefault();
                  runMobileCommandSearch();
                }}
              >
                <Search size={18} className="text-cyan-300" />
                <input
                  value={mobileCommandValue}
                  onChange={(event) => setMobileCommandValue(event.target.value)}
                  placeholder={mobileCommandPlaceholder}
                  disabled={mobileCommandTarget === "orders"}
                  className="min-w-0 flex-1 bg-transparent text-base font-semibold text-white outline-none placeholder:text-neutral-600"
                />
                {hasActiveSearch && (
                  <button type="button" onClick={clearHeaderSearch} className="text-neutral-500 transition hover:text-white" aria-label="Clear Sentinel search">
                    <XCircle size={18} />
                  </button>
                )}
              </form>
            )}
          </div>

            <div className="relative grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-0 xl:grid-cols-[248px_minmax(0,1fr)] xl:grid-rows-none">
            <aside className="shrink-0 overflow-hidden border-b border-white/[0.06] bg-black/10 p-3 backdrop-blur-xl xl:border-b-0 xl:border-r xl:border-white/[0.06] xl:p-4">
              <div className="hidden-scrollbar flex gap-2 overflow-x-auto rounded-2xl border border-white/[0.055] bg-white/[0.025] p-1.5 xl:grid xl:grid-cols-1 xl:overflow-visible">
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex min-h-11 shrink-0 items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-sm font-bold transition xl:w-full ${
                        active
                          ? "border-cyan-300/30 bg-cyan-300/12 text-white shadow-[0_0_28px_rgba(34,211,238,0.08)]"
                          : "border-transparent bg-transparent text-neutral-400 hover:border-white/10 hover:bg-white/[0.04] hover:text-neutral-100"
                      }`}
                    >
                      <span>{tab.label}</span>
                      <Icon size={16} className={active ? "text-cyan-200" : "text-neutral-500"} />
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="sentinel-scroll h-full overflow-y-auto p-3 pb-10 sm:p-6 sm:pb-10">
              {error && (
                <div className="mb-4 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}

              {activeTab === "pulse" && (
                <PulsePanel analytics={analytics} loading={analyticsLoading} />
              )}

              {activeTab === "metrics" && (
                <MetricsPanel analytics={analytics} loading={analyticsLoading} />
              )}

              {activeTab === "blackbox" && (
                <BlackboxPanel
                  cases={cases}
                  caseCounts={caseCounts}
                  caseQuery={caseQuery}
                  caseStatus={caseStatus}
                  casesLoading={casesLoading}
                  busyId={busyId}
                  realm={realm}
                  replyDrafts={replyDrafts}
                  reopenMenu={reopenMenu}
                  onQuery={setCaseQuery}
                  onStatus={setCaseStatus}
                  onSearch={() => loadCases(caseStatus, caseQuery)}
                  onPublish={publishCase}
                  onChangeStatus={changeCaseStatus}
                  onReplyDraft={setReplyDrafts}
                  onReply={sendPresenceReply}
                  onReopenMenu={setReopenMenu}
                />
              )}

              {activeTab === "support" && <SupportQueue active canReply={hasPermission(realm, "support:reply")} canChangeStatus={hasPermission(realm, "support:status")} />}

              {activeTab === "realm" && (
                <RealmPanel
                  users={realmUsers}
                  loading={usersLoading}
                  query={usersQuery}
                  realm={realm}
                  statusReasons={statusReasons}
                  roleReasons={roleReasons}
                  rowToasts={rowToasts}
                  onQuery={setUsersQuery}
                  onSearch={loadUsers}
                  onReason={setStatusReasons}
                  onRoleReason={setRoleReasons}
                  onStatus={updateUserStatus}
                  onRole={updateUserRole}
                  onProtection={updateUserProtection}
                  currentUserId={currentUserId}
                />
              )}

              {activeTab === "permissions" && (
                <PermissionsPanel
                  users={accessUsers}
                  loading={accessLoading}
                  query={accessQuery}
                  realm={realm}
                  refreshing={refreshingRealm}
                  rowToasts={rowToasts}
                  currentUserId={currentUserId}
                  onQuery={setAccessQuery}
                  onSearch={() => loadAccessUsers(accessQuery)}
                  onRole={updateUserRole}
                  onProtection={updateUserProtection}
                  onRefresh={refreshRealm}
                />
              )}

              {activeTab === "underground" && (
                <UndergroundPanel
                  users={undergroundUsers}
                  loading={undergroundLoading}
                  query={undergroundQuery}
                  status={undergroundStatus}
                  realm={realm}
                  statusReasons={statusReasons}
                  rowToasts={rowToasts}
                  currentUserId={currentUserId}
                  onQuery={setUndergroundQuery}
                  onStatusFilter={setUndergroundStatus}
                  onSearch={() => loadUndergroundUsers(undergroundQuery, undergroundStatus)}
                  onReason={setStatusReasons}
                  onStatus={updateUserStatus}
                />
              )}

              {activeTab === "orders" && (
                <OrdersPanel
                  orders={orders}
                  loading={ordersLoading}
                  realm={realm}
                  note={interventionNote}
                  onNote={setInterventionNote}
                  onCreate={createIntervention}
                />
              )}

              {activeTab === "reports" && (
                <ReportsPanel
                  reports={reports}
                  summary={reportsSummary}
                  loading={reportsLoading}
                  status={reportStatus}
                  query={reportQuery}
                  realm={realm}
                  onStatus={setReportStatus}
                  onQuery={setReportQuery}
                  onSearch={() => loadReports(reportStatus, reportQuery)}
                  onUpdate={updateReportStatus}
                />
              )}
              </div>
              <div className="pointer-events-none absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-black/50 to-transparent" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/70 to-transparent" />
            </section>
          </div>
        </section>
        <style>{`.case-action{display:inline-flex;align-items:center;gap:.5rem;border-width:1px;border-radius:.85rem;padding:.55rem .8rem;font-size:.75rem;font-weight:900;transition:background-color .15s,border-color .15s,opacity .15s,transform .15s}.case-action-icon{width:2.45rem;height:2.45rem;justify-content:center;padding:0}.case-action:hover{transform:translateY(-1px)}.case-action:disabled{cursor:not-allowed;opacity:.38;transform:none}.sentinel-scroll,.sentinel-scroll *{scrollbar-width:none;-ms-overflow-style:none}.sentinel-scroll::-webkit-scrollbar,.sentinel-scroll *::-webkit-scrollbar{display:none;width:0;height:0}.bs-sentinel-shell input,.bs-sentinel-shell textarea,.bs-sentinel-shell select{border-radius:.9rem;background:rgba(0,0,0,.38)!important;border-color:rgba(255,255,255,.075)!important}.bs-sentinel-shell article,.bs-sentinel-shell section.rounded-lg,.bs-sentinel-shell .rounded-lg{border-radius:1.15rem!important;border-color:rgba(255,255,255,.075)!important;background:linear-gradient(145deg,rgba(255,255,255,.045),rgba(255,255,255,.018))!important;box-shadow:0 18px 60px rgba(0,0,0,.18),inset 0 1px 0 rgba(255,255,255,.035)}.bs-sentinel-shell .rounded-md{border-radius:.85rem}.bs-sentinel-shell h2{letter-spacing:-.02em}.bs-sentinel-shell article:hover,.bs-sentinel-shell section.rounded-lg:hover{border-color:rgba(34,211,238,.2)!important}`}</style>
      </main>
      <FooterNav />
    </div>
  );
}

function PulsePanel({ analytics, loading }: { analytics: Analytics | null; loading: boolean }) {
  if (loading && !analytics) return <LoadingPanel label="Reading pulse..." />;
  if (!analytics) return <EmptyPanel icon={BarChart3} title="Pulse is quiet." body="No analytics are available yet." />;

  const cards = [
    { key: "users" as const, label: "Users", value: analytics.totals.users, icon: Users, tone: "text-cyan-200" },
    { key: "posts" as const, label: "Posts", value: analytics.totals.posts, icon: Send, tone: "text-fuchsia-200" },
    { key: "spaces" as const, label: "Spaces", value: analytics.totals.spaces, icon: ShieldCheck, tone: "text-emerald-200" },
    { key: "messageMetadata" as const, label: "Messages", value: analytics.totals.messageMetadata, icon: MessageSquareReply, tone: "text-amber-200" },
    { key: "blackbox" as const, label: "Blackbox cases", value: analytics.totals.blackbox, icon: Box, tone: "text-white" },
    { key: "conversations" as const, label: "Conversations", value: analytics.totals.conversations, icon: ScrollText, tone: "text-neutral-200" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map(({ key, label, value, icon: Icon, tone }) => {
          const signal = key === "blackbox";
          return (
          <div key={label} className="rounded-lg border border-white/10 bg-black/35 p-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-neutral-500">{label}</span>
              <Icon size={18} className={tone} />
            </div>
            <div className="flex items-end justify-between gap-3">
              <p className="text-3xl font-black text-white">{compactNumber(value)}</p>
              <span className={`rounded-md border px-2 py-1 text-xs font-black ${changeTone(analytics.changes[key], signal ? "signal" : "good")}`}>
                {formatChange(analytics.changes[key])}
              </span>
            </div>
            <p className="mt-2 text-xs text-neutral-600">vs previous 7 days</p>
          </div>
        );})}
      </div>

      <div className="grid gap-3 lg:grid-cols-[1fr_1.15fr]">
        <div className="rounded-lg border border-white/10 bg-black/35 p-4">
          <h2 className="text-sm font-black text-white">Last 7 days</h2>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Metric label="New users" value={analytics.sevenDays.users} />
            <Metric label="New posts" value={analytics.sevenDays.posts} />
            <Metric label="Messages" value={analytics.sevenDays.messageMetadata} />
            <Metric label="Blackbox" value={analytics.sevenDays.blackbox} />
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/35 p-4">
          <h2 className="text-sm font-black text-white">Blackbox status</h2>
          <div className="mt-4 space-y-2">
            {analytics.blackboxByStatus.map((row) => (
              <div key={row.status} className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.04] px-3 py-2">
                <span className="text-sm text-neutral-300">{statusLabel(row.status)}</span>
                <span className="text-sm font-black text-white">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.04] p-3">
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="mt-1 text-xl font-black text-white">{value}</p>
    </div>
  );
}

function MetricsPanel({ analytics, loading }: { analytics: Analytics | null; loading: boolean }) {
  if (loading && !analytics) return <LoadingPanel label="Drawing metrics..." />;
  if (!analytics) return <EmptyPanel icon={Activity} title="Metrics are quiet." body="No metrics are available yet." />;

  const trendSeries = [
    { key: "users" as const, label: "Users", color: "#67e8f9" },
    { key: "posts" as const, label: "Posts", color: "#f0abfc" },
    { key: "messages" as const, label: "Messages", color: "#fcd34d" },
    { key: "blackbox" as const, label: "Blackbox", color: "#a7f3d0" },
  ];

  return (
    <div className="space-y-4 pb-8">
      <div className="grid gap-3 xl:grid-cols-[1.4fr_.85fr]">
        <section className="rounded-lg border border-white/10 bg-black/35 p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-white">Growth</h2>
              <p className="mt-1 text-sm text-neutral-500">Fourteen-day movement across the living surface.</p>
            </div>
            <Activity className="text-cyan-200" size={20} />
          </div>
          <MultiLineChart data={analytics.metrics.trends} series={trendSeries} />
          <div className="mt-4 flex flex-wrap gap-2">
            {trendSeries.map((item) => (
              <span key={item.key} className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs font-bold text-neutral-300">
                <span className="h-2 w-2 rounded-sm" style={{ background: item.color }} />
                {item.label}
              </span>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-fuchsia-300/15 bg-[radial-gradient(circle_at_top_right,rgba(217,70,239,0.16),transparent_35%),rgba(0,0,0,0.35)] p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-white">Momentum</h2>
              <p className="mt-1 text-sm text-neutral-500">This week against the one before.</p>
            </div>
            <TrendingUp className="text-fuchsia-100" size={20} />
          </div>
          <div className="space-y-3">
            {analytics.momentum.map((item) => (
              <div key={item.label} className="rounded-md border border-white/10 bg-black/35 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">{item.label}</span>
                  <span className={`rounded-md border px-2 py-1 text-xs font-black ${changeTone(item.value, item.label.includes("Blackbox") || item.label.includes("Reports") ? "signal" : "good")}`}>
                    {formatChange(item.value)}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-white/[0.05]">
                  <div className="h-full rounded-full bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-emerald-200" style={{ width: `${Math.min(100, Math.max(8, Math.abs(item.value)))}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid gap-3 xl:grid-cols-3">
        <section className="rounded-lg border border-white/10 bg-black/35 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-black text-white">Blackbox trust flow</h2>
            <Box className="text-cyan-100" size={18} />
          </div>
          <FunnelBars items={analytics.metrics.blackboxFunnel.map((item) => ({ label: statusLabel(item.status), value: item.count }))} tone="cyan" />
        </section>

        <section className="rounded-lg border border-white/10 bg-black/35 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-black text-white">Reports signal</h2>
            <Flag className="text-fuchsia-100" size={18} />
          </div>
          <FunnelBars items={analytics.reportsByStatus.map((item) => ({ label: reportStatusLabel(item.status), value: item.count }))} tone="fuchsia" />
        </section>

        <section className="rounded-lg border border-white/10 bg-black/35 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-black text-white">Severity mix</h2>
            <PieChart className="text-amber-100" size={18} />
          </div>
          <SeverityOrbit items={analytics.reportsBySeverity.map((item) => ({ label: item.severity, value: item.count }))} />
        </section>
      </div>

      <div className="grid gap-3 xl:grid-cols-[1.1fr_.9fr]">
        <section className="rounded-lg border border-white/10 bg-black/35 p-4">
          <h2 className="text-sm font-black text-white">Space gravity</h2>
          <div className="mt-4 space-y-2">
            {analytics.metrics.topSpaces.length === 0 ? (
              <p className="text-sm text-neutral-500">No space activity yet.</p>
            ) : analytics.metrics.topSpaces.map((space, index) => {
              const max = Math.max(1, ...analytics.metrics.topSpaces.map((item) => item.posts + item.reports + item.members));
              const width = Math.max(8, ((space.posts + space.reports + space.members) / max) * 100);
              return (
                <div key={space.id} className="rounded-md border border-white/10 bg-white/[0.035] p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-sm font-black text-white">{index + 1}. {space.title}</span>
                    <span className="text-xs text-neutral-500">{space.posts} posts · {space.reports} reports · {space.members} members</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                    <div className="h-full rounded-full bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-emerald-200" style={{ width: `${width}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-lg border border-white/10 bg-black/35 p-4">
          <h2 className="text-sm font-black text-white">Activity rhythm</h2>
          <p className="mt-1 text-xs text-neutral-500">Message metadata only, grouped by day and hour.</p>
          <RhythmGrid items={analytics.metrics.activityRhythm} />
        </section>
      </div>
    </div>
  );
}

function MultiLineChart({
  data,
  series,
}: {
  data: Analytics["metrics"]["trends"];
  series: { key: "users" | "posts" | "messages" | "blackbox"; label: string; color: string }[];
}) {
  const width = 720;
  const height = 260;
  const pad = 22;
  const max = Math.max(1, ...data.flatMap((point) => series.map((item) => Number(point[item.key]) || 0)));
  const x = (index: number) => pad + (index / Math.max(1, data.length - 1)) * (width - pad * 2);
  const y = (value: number) => height - pad - (value / max) * (height - pad * 2);

  return (
    <div className="overflow-hidden rounded-lg border border-white/10 bg-black/30">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-72 w-full">
        <defs>
          <linearGradient id="metric-glow" x1="0" x2="1">
            <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.16" />
            <stop offset="50%" stopColor="#f0abfc" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#a7f3d0" stopOpacity="0.16" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width={width} height={height} fill="url(#metric-glow)" />
        {[0.25, 0.5, 0.75].map((line) => (
          <line key={line} x1={pad} x2={width - pad} y1={pad + (height - pad * 2) * line} y2={pad + (height - pad * 2) * line} stroke="rgba(255,255,255,.08)" />
        ))}
        {series.map((item) => {
          const d = data.map((point, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(Number(point[item.key]) || 0)}`).join(" ");
          return <path key={item.key} d={d} fill="none" stroke={item.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />;
        })}
      </svg>
    </div>
  );
}

function FunnelBars({ items, tone }: { items: { label: string; value: number }[]; tone: "cyan" | "fuchsia" }) {
  const max = Math.max(1, ...items.map((item) => item.value));
  const gradient = tone === "cyan" ? "from-cyan-200 to-emerald-200" : "from-fuchsia-200 to-cyan-200";
  return (
    <div className="space-y-2">
      {items.filter((item) => item.value > 0).slice(0, 7).map((item) => (
        <div key={item.label}>
          <div className="mb-1 flex items-center justify-between gap-2 text-xs">
            <span className="truncate text-neutral-400">{item.label}</span>
            <span className="font-black text-white">{item.value}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
            <div className={`h-full rounded-full bg-gradient-to-r ${gradient}`} style={{ width: `${Math.max(8, (item.value / max) * 100)}%` }} />
          </div>
        </div>
      ))}
      {items.every((item) => item.value === 0) && <p className="text-sm text-neutral-500">No signal yet.</p>}
    </div>
  );
}

function SeverityOrbit({ items }: { items: { label: string; value: number }[] }) {
  const total = Math.max(1, items.reduce((sum, item) => sum + item.value, 0));
  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map((item) => {
        const pct = Math.round((item.value / total) * 100);
        const tone = item.label === "high" ? "border-red-300/20 text-red-100" : item.label === "medium" ? "border-amber-300/20 text-amber-100" : "border-cyan-300/20 text-cyan-100";
        return (
          <div key={item.label} className={`rounded-lg border bg-white/[0.035] p-3 text-center ${tone}`}>
            <p className="text-2xl font-black">{pct}%</p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">{item.label}</p>
            <p className="mt-1 text-xs text-neutral-400">{item.value}</p>
          </div>
        );
      })}
      {items.length === 0 && <p className="col-span-3 text-sm text-neutral-500">No severity data yet.</p>}
    </div>
  );
}

function RhythmGrid({ items }: { items: { day: number; hour: number; count: number }[] }) {
  const max = Math.max(1, ...items.map((item) => item.count));
  const lookup = new Map(items.map((item) => [`${item.day}:${Math.floor(item.hour / 3)}`, item.count]));
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="mt-4 space-y-2">
      {days.map((day, dayIndex) => (
        <div key={day} className="grid grid-cols-[38px_repeat(8,minmax(0,1fr))] items-center gap-1">
          <span className="text-[11px] font-bold text-neutral-600">{day}</span>
          {Array.from({ length: 8 }, (_, bucket) => {
            const count = lookup.get(`${dayIndex + 1}:${bucket}`) || 0;
            const opacity = count ? 0.18 + (count / max) * 0.72 : 0.05;
            return <span key={bucket} title={`${count} messages`} className="h-6 rounded-md border border-white/5" style={{ background: `rgba(103,232,249,${opacity})` }} />;
          })}
        </div>
      ))}
    </div>
  );
}

function BlackboxPanel(props: {
  cases: BlackboxAdminTransmission[];
  caseCounts: string;
  caseQuery: string;
  caseStatus: BlackboxAdminStatus;
  casesLoading: boolean;
  busyId: string | null;
  realm: Realm | null;
  replyDrafts: Record<string, string>;
  reopenMenu: string | null;
  onQuery: (value: string) => void;
  onStatus: (value: BlackboxAdminStatus) => void;
  onSearch: () => void;
  onPublish: (item: BlackboxAdminTransmission) => void;
  onChangeStatus: (item: BlackboxAdminTransmission, status: BlackboxCaseStatus) => void;
  onReplyDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onReply: (item: BlackboxAdminTransmission) => void;
  onReopenMenu: (id: string | null) => void;
}) {
  const {
    cases,
    caseCounts,
    caseQuery,
    caseStatus,
    casesLoading,
    busyId,
    realm,
    replyDrafts,
    reopenMenu,
    onQuery,
    onStatus,
    onSearch,
    onPublish,
    onChangeStatus,
    onReplyDraft,
    onReply,
    onReopenMenu,
  } = props;

  return (
    <div className="pb-8">
      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Blackbox cases</h2>
          <p className="mt-1 text-sm text-neutral-500">In view: {caseCounts}</p>
        </div>
        <form
          className="bs-sentinel-panel-search flex flex-col gap-2 sm:flex-row"
          onSubmit={(event) => {
            event.preventDefault();
            onSearch();
          }}
        >
          <label className="relative min-w-0 sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={caseQuery}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Search receipt, shared code, contact, message..."
              className="h-10 w-full rounded-md border border-white/10 bg-black/45 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-300/45"
            />
          </label>
          <button className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cyan-200 text-sm font-black text-black transition hover:bg-white" title="Search Blackbox cases" aria-label="Search Blackbox cases">
            <Search size={15} />
          </button>
        </form>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
        {statusOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => onStatus(option.value)}
            className={`shrink-0 rounded-md border px-3 py-2 text-xs font-black transition ${
              caseStatus === option.value
                ? "border-cyan-300/30 bg-cyan-300/10 text-white"
                : "border-white/10 bg-black/30 text-neutral-400 hover:text-white"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {casesLoading ? (
        <LoadingPanel label="Reading Blackbox cases..." />
      ) : cases.length === 0 ? (
        <EmptyPanel icon={Box} title="No cases here." body="Change the filter or wait for the Blackbox to speak." />
      ) : (
        <div className="space-y-3 pb-8">
          {cases.map((item) => {
            const closed = item.status === "closed";
            const posted = item.status === "posted" || Boolean(item.publishedPost);
            const sealed = Boolean(item.sealedByPresence);
            const expired = Boolean(item.expired);
            const lockedForRole = sealed && !realm?.isPresence;
            const disabled = busyId === item.id || closed || posted || lockedForRole || !hasPermission(realm, "blackbox:status");
            return (
              <article key={item.id} className={`rounded-lg border bg-black/35 p-4 transition ${closed ? "border-neutral-800 opacity-80" : "border-white/10 hover:border-cyan-300/25"}`}>
                <div className="flex flex-col gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs font-black text-white">{item.receiptCode}</span>
                      {item.codeType === "custom" && <span className="rounded-md border border-violet-300/20 bg-violet-300/10 px-2.5 py-1 text-xs font-bold text-violet-100">Shared code</span>}
                      {item.codeType === "custom" && <span className="rounded-md border border-cyan-300/20 bg-cyan-300/10 px-2.5 py-1 text-xs font-bold text-cyan-100">{item.accessMode === "read_only" ? "Read only" : "Read & write"}</span>}
                      {expired && <span className="rounded-md border border-neutral-500/30 bg-neutral-700/30 px-2.5 py-1 text-xs font-bold text-neutral-300">Expired</span>}
                      <span className={`rounded-md border px-2.5 py-1 text-xs font-bold ${statusTone(item.status)}`}>{item.statusLabel || statusLabel(item.status)}</span>
                      {sealed && <span className="rounded-md border border-cyan-200/25 bg-cyan-200/10 px-2.5 py-1 text-xs font-black text-cyan-100">Locked</span>}
                      <span className="rounded-md border border-fuchsia-300/20 bg-fuchsia-300/10 px-2.5 py-1 text-xs font-bold text-fuchsia-100">{item.category}</span>
                      {item.appealCount > 0 && <span className="rounded-md border border-amber-300/20 bg-amber-300/10 px-2.5 py-1 text-xs font-bold text-amber-100">Appeals {item.appealCount}</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold text-neutral-500">
                      <span>Received {formatDate(item.createdAt)}</span>
                      <span>Reviewed {formatDate(item.reviewedAt)}</span>
                      {item.expiresAt && <span className={expired ? "text-neutral-500" : "text-violet-200"}>{expired ? "Expired" : "Expires"} {formatDate(item.expiresAt)}</span>}
                      {item.publishedAt && <span className="text-emerald-200">Posted {formatDate(item.publishedAt)}</span>}
                    </div>
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-neutral-200">{item.message}</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 text-xs text-neutral-400">
                  <span className="max-w-full truncate rounded-md border border-white/10 bg-black/35 px-2.5 py-1.5">Contact: {item.contact || "None"}</span>
                  <span className="max-w-full truncate rounded-md border border-white/10 bg-black/35 px-2.5 py-1.5">Locale: {item.locale || "Unknown"}</span>
                  <span className="max-w-full truncate rounded-md border border-white/10 bg-black/35 px-2.5 py-1.5">Timezone: {item.timezone || "Unknown"}</span>
                </div>

                {(item.thread || []).length > 0 && (
                  <div className="mt-4 max-h-52 space-y-2 overflow-y-auto rounded-md border border-white/10 bg-black/45 p-2">
                    {item.thread.map((entry) => (
                      <div key={entry.id} className={`rounded-md border px-3 py-2 ${entry.author === "presence" ? "border-cyan-300/20 bg-cyan-300/10" : "border-white/10 bg-white/[0.04]"}`}>
                        <div className="mb-1 flex items-center justify-between text-[11px] font-black uppercase tracking-[0.14em]">
                          <span className={entry.author === "presence" ? "text-cyan-100" : "text-neutral-400"}>{entry.author === "presence" ? "Blackbox" : entry.participantRole === "sender" || entry.participantRole === "creator" ? "Sender" : entry.participantRole === "receiver" || entry.participantRole === "participant" ? "Receiver" : "Transmitter"}</span>
                          <span className="font-normal normal-case tracking-normal text-neutral-600">{formatDate(entry.createdAt)}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-xs leading-5 text-neutral-200">{entry.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-stretch gap-2">
                  <textarea
                    value={replyDrafts[item.id] || ""}
                    onChange={(event) => onReplyDraft((current) => ({ ...current, [item.id]: event.target.value.slice(0, 3000) }))}
                    disabled={closed || lockedForRole || !hasPermission(realm, "blackbox:reply")}
                    placeholder={lockedForRole ? "This verdict is locked." : closed ? "Closed cases must be reopened before replying." : "Reply to the receipt..."}
                    rows={2}
                    className="min-w-0 flex-1 resize-none rounded-md border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-45"
                  />
                  <button
                    disabled={busyId === item.id || closed || lockedForRole || !hasPermission(realm, "blackbox:reply") || (replyDrafts[item.id] || "").trim().length < 2}
                    onClick={() => onReply(item)}
                    title="Reply to this Blackbox case"
                    aria-label="Reply to this Blackbox case"
                    className="bs-premium-header-action case-action-icon inline-flex min-h-10 shrink-0 items-center justify-center rounded-md border border-cyan-300/20 bg-cyan-300/10 text-xs font-black text-cyan-100 transition hover:bg-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <MessageSquareReply size={15} />
                  </button>
                </div>

                <div className="mt-4 flex flex-nowrap gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
                  <button title="Move case to review" aria-label="Move case to review" disabled={disabled || item.status === "in_review"} onClick={() => onChangeStatus(item, "in_review")} className="case-action case-action-icon shrink-0 border-amber-300/20 bg-amber-300/10 text-amber-100">
                    <Clock3 size={15} />
                  </button>
                  <button title="Reject this case" aria-label="Reject this case" disabled={disabled || item.status === "rejected"} onClick={() => onChangeStatus(item, "rejected")} className="case-action case-action-icon shrink-0 border-red-300/20 bg-red-400/10 text-red-100">
                    <XCircle size={15} />
                  </button>
                  <button title="Publish this case" aria-label="Publish this case" disabled={busyId === item.id || closed || posted || sealed || !realm?.isPresence} onClick={() => onPublish(item)} className="case-action case-action-icon shrink-0 border-emerald-300/20 bg-emerald-300/10 text-emerald-100">
                    <Send size={15} />
                  </button>
                  <button title="Close this case" aria-label="Close this case" disabled={busyId === item.id || closed || posted} onClick={() => onChangeStatus(item, "closed")} className="case-action case-action-icon shrink-0 border-neutral-500/25 bg-neutral-700/30 text-neutral-200">
                    <Ban size={15} />
                  </button>
                  {(closed || sealed) && realm?.isPresence && (
                    <div className="relative shrink-0">
                      <button title="Reopen this case" aria-label="Reopen this case" onClick={() => onReopenMenu(reopenMenu === item.id ? null : item.id)} className="case-action case-action-icon border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
                        <MoreHorizontal size={15} />
                      </button>
                      {reopenMenu === item.id && (
                        <div className="absolute right-0 top-11 z-[2700] flex gap-2 rounded-2xl border border-white/10 bg-neutral-950 p-2 shadow-2xl">
                          {(["received", "in_review", "rejected"] as BlackboxCaseStatus[]).map((status) => (
                            <button key={status} title={`Reopen as ${statusLabel(status)}`} aria-label={`Reopen as ${statusLabel(status)}`} onClick={() => onChangeStatus(item, status)} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-neutral-300 hover:bg-white/10 hover:text-white">
                              {status === "in_review" ? <Clock3 size={14} /> : status === "rejected" ? <XCircle size={14} /> : <RotateCcw size={14} />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RealmPanel(props: {
  users: RealmUser[];
  loading: boolean;
  query: string;
  realm: Realm | null;
  statusReasons: Record<string, string>;
  roleReasons: Record<string, string>;
  rowToasts: Record<string, string>;
  currentUserId: string;
  onQuery: (value: string) => void;
  onSearch: () => void;
  onReason: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onRoleReason: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onStatus: (user: RealmUser, status: RealmUser["realmStatus"]) => void;
  onRole: (user: RealmUser, role: RealmUser["role"], options?: GrantOptions) => void;
  onProtection: (user: RealmUser, protectedState: boolean) => void;
}) {
  const canWrite = hasPermission(props.realm, "users:write");
  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Members and roles</h2>
          <p className="mt-1 text-sm text-neutral-500">Search active owners and admins. Members stay out of this list until they are granted a role.</p>
        </div>
        <form
          className="bs-sentinel-panel-search flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            props.onSearch();
          }}
        >
          <input value={props.query} onChange={(event) => props.onQuery(event.target.value)} placeholder="Search role holders..." className="h-10 min-w-0 rounded-md border border-white/10 bg-black/45 px-3 text-sm text-white outline-none focus:border-cyan-300/45" />
          <button className="inline-flex h-10 items-center gap-2 rounded-md bg-cyan-200 px-4 text-sm font-black text-black"><Search size={15} /> Search</button>
        </form>
      </div>

      {props.loading ? <LoadingPanel label="Reading member roles..." /> : props.users.length === 0 ? (
        <EmptyPanel icon={UserCog} title="No role holders in view." body="Only active owners and admins appear here." />
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {props.users.map((user) => {
            const isSelf = Boolean(props.currentUserId && user.id === props.currentUserId);
            const blockedByProtection = Boolean(user.protected && !props.realm?.isPresence);
            const blockedByAccessLock = Boolean(user.isPresence || user.grantLocked);
            const canGovernUser = canWrite && !isSelf && !blockedByProtection && !blockedByAccessLock;
            const canChangeRole = hasPermission(props.realm, "permissions:write") && !isSelf && !blockedByProtection && !blockedByAccessLock && (props.realm?.isPresence || user.role === "user" || user.role === "admin");
            const roleUnavailable = hasPermission(props.realm, "permissions:write") && !canChangeRole;
            return (
            <div key={user.id} className={`rounded-lg border border-white/10 bg-black/35 p-4 ${isSelf ? "opacity-75" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05] text-sm font-black text-cyan-100">{(user.displayName || user.username).slice(0, 1).toUpperCase()}</div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="truncate text-sm font-black text-white">{user.displayName || user.username}</h3>
                    </div>
                    <p className="truncate text-xs text-neutral-500">@{user.username}</p>
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <span className={`rounded-md border px-2 py-1 text-xs font-black ${user.realmStatus === "banned" ? "border-red-300/25 bg-red-400/10 text-red-100" : user.realmStatus === "restricted" ? "border-amber-300/25 bg-amber-300/10 text-amber-100" : "border-emerald-300/25 bg-emerald-300/10 text-emerald-100"}`}>{user.realmStatus}</span>
                  <span className="rounded-md border border-cyan-300/20 bg-cyan-300/10 px-2 py-1 text-xs font-black text-cyan-100">{roleLabel(user.role)}</span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <Metric label="Posts" value={user.publicActivity.posts} />
                <Metric label="Messages" value={user.publicActivity.messagesSent} />
                <Metric label="Spaces" value={user.publicActivity.spacesJoined} />
              </div>

              {canWrite && (
                <div className="mt-4 space-y-3">
                  <input
                    value={props.statusReasons[user.id] ?? user.realmReason ?? ""}
                    onChange={(event) => props.onReason((current) => ({ ...current, [user.id]: event.target.value }))}
                    disabled={!canGovernUser}
                    placeholder={isSelf ? "Self-governance is disabled" : "Reason for action..."}
                    className="h-10 w-full rounded-md border border-white/10 bg-black px-3 text-xs text-white outline-none focus:border-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-45"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button disabled={!canGovernUser || user.realmStatus === "active"} onClick={() => props.onStatus(user, "active")} className="case-action border-emerald-300/20 bg-emerald-300/10 text-emerald-100"><CheckCircle2 size={15} /> Restore</button>
                    <button disabled={!canGovernUser || user.realmStatus === "restricted"} onClick={() => props.onStatus(user, "restricted")} className="case-action border-amber-300/20 bg-amber-300/10 text-amber-100"><Shield size={15} /> Restrict</button>
                    <button disabled={!canGovernUser || user.realmStatus === "banned"} onClick={() => props.onStatus(user, "banned")} className="case-action border-red-300/20 bg-red-400/10 text-red-100"><Ban size={15} /> Ban</button>
                  </div>
                </div>
              )}

              {hasPermission(props.realm, "permissions:write") && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <UserCog size={16} className="text-neutral-500" />
                  {!props.realm?.isPresence && canChangeRole && (
                    <input
                      value={props.roleReasons[user.id] || ""}
                      onChange={(event) => props.onRoleReason((current) => ({ ...current, [user.id]: event.target.value }))}
                      placeholder="Reason required..."
                      className="h-10 w-44 rounded-md border border-white/10 bg-black px-3 text-xs text-white outline-none focus:border-cyan-300/45"
                    />
                  )}
                  {canChangeRole ? (
                    <select value={user.role} onChange={(event) => props.onRole(user, event.target.value as RealmUser["role"])} className="h-10 rounded-md border border-white/10 bg-black px-3 text-xs font-black text-white outline-none focus:border-cyan-300/45">
                      {!roleOptionsFor(props.realm).includes(user.role) && <option value={user.role}>{roleLabel(user.role)}</option>}
                      {roleOptionsFor(props.realm).map((role) => (
                        <option key={role} value={role}>{roleLabel(role)}</option>
                      ))}
                    </select>
                  ) : roleUnavailable ? (
                    <select disabled value={user.role} className="h-10 rounded-md border border-white/10 bg-white/[0.03] px-3 text-xs font-black text-neutral-600 outline-none">
                      <option value={user.role}>{roleLabel(user.role)}</option>
                    </select>
                  ) : null}
                  {props.realm?.isPresence && !isSelf && !user.grantLocked && (
                    <button
                      onClick={() => props.onProtection(user, !user.protected)}
                      className={`inline-flex h-10 items-center gap-2 rounded-md border px-3 text-xs font-black transition ${
                        user.protected
                          ? "border-emerald-300/20 bg-emerald-300/10 text-emerald-100 hover:bg-emerald-300/20"
                          : "border-white/10 bg-white/[0.04] text-neutral-300 hover:border-emerald-300/25 hover:text-white"
                      }`}
                    >
                      <ShieldCheck size={14} />
                      {user.protected ? "Protected" : "Protect"}
                    </button>
                  )}
                  {props.rowToasts[user.id] && (
                    <span className="inline-flex h-10 items-center rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 text-xs font-black text-cyan-100">
                      {props.rowToasts[user.id]}
                    </span>
                  )}
                  {user.grantLocked && (
                    <span className="inline-flex h-10 items-center rounded-md border border-white/10 bg-white/[0.03] px-3 text-xs font-black text-neutral-500">
                      Access locked
                    </span>
                  )}
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function UndergroundPanel(props: {
  users: RealmUser[];
  loading: boolean;
  query: string;
  status: "all" | "restricted" | "banned";
  realm: Realm | null;
  statusReasons: Record<string, string>;
  rowToasts: Record<string, string>;
  currentUserId: string;
  onQuery: (value: string) => void;
  onStatusFilter: (value: "all" | "restricted" | "banned") => void;
  onSearch: () => void;
  onReason: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onStatus: (user: RealmUser, status: RealmUser["realmStatus"]) => void;
}) {
  const canWrite = hasPermission(props.realm, "users:write");
  const filters: Array<{ value: "all" | "restricted" | "banned"; label: string }> = [
    { value: "all", label: "All" },
    { value: "restricted", label: "Restricted" },
    { value: "banned", label: "Banned" },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Restricted users</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Review restricted and banned users, or search any username to apply a safety action.
          </p>
        </div>
        <div className="flex flex-col gap-2 lg:items-end">
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => props.onStatusFilter(filter.value)}
                className={`h-9 rounded-md border px-3 text-xs font-black transition ${
                  props.status === filter.value
                    ? "border-cyan-300/35 bg-cyan-300/10 text-cyan-100"
                    : "border-white/10 bg-white/[0.04] text-neutral-400 hover:border-white/20 hover:text-white"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <form
            className="bs-sentinel-panel-search flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              props.onSearch();
            }}
          >
            <input
              value={props.query}
              onChange={(event) => props.onQuery(event.target.value)}
              placeholder="Search users to govern..."
              className="h-10 min-w-0 rounded-md border border-white/10 bg-black/45 px-3 text-sm text-white outline-none focus:border-cyan-300/45"
            />
            <button className="inline-flex h-10 items-center gap-2 rounded-md bg-cyan-200 px-4 text-sm font-black text-black">
              <Search size={15} /> Search
            </button>
          </form>
        </div>
      </div>

      {props.loading ? <LoadingPanel label="Reading restricted users..." /> : props.users.length === 0 ? (
        <EmptyPanel icon={Ban} title="No users in view." body="Restricted and banned users appear here. Search a username to act directly." />
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {props.users.map((user) => {
            const isSelf = Boolean(props.currentUserId && user.id === props.currentUserId);
            const blockedByProtection = Boolean(user.protected && !props.realm?.isPresence);
            const blockedByAccessLock = Boolean(user.isPresence || user.grantLocked);
            const canGovernUser = canWrite && !isSelf && !blockedByProtection && !blockedByAccessLock;
            const statusToneClass = user.realmStatus === "banned"
              ? "border-red-300/25 bg-red-400/10 text-red-100"
              : user.realmStatus === "restricted"
                ? "border-amber-300/25 bg-amber-300/10 text-amber-100"
                : "border-emerald-300/25 bg-emerald-300/10 text-emerald-100";
            return (
              <article key={user.id} className={`rounded-lg border p-4 ${user.realmStatus === "active" ? "border-white/10 bg-black/35" : "border-red-300/10 bg-red-950/10"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05] text-sm font-black text-cyan-100">
                      {(user.displayName || user.username).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-sm font-black text-white">{user.displayName || user.username}</h3>
                      </div>
                      <p className="truncate text-xs text-neutral-500">@{user.username}</p>
                    </div>
                  </div>
                  <span className={`rounded-md border px-2.5 py-1 text-xs font-black ${statusToneClass}`}>
                    {user.realmStatus}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Metric label="Posts" value={user.publicActivity.posts} />
                  <Metric label="Messages" value={user.publicActivity.messagesSent} />
                  <Metric label="Spaces" value={user.publicActivity.spacesJoined} />
                </div>

                <div className="mt-4 space-y-3">
                  <input
                    value={props.statusReasons[user.id] ?? user.realmReason ?? ""}
                    onChange={(event) => props.onReason((current) => ({ ...current, [user.id]: event.target.value }))}
                    disabled={!canGovernUser}
                    placeholder={props.realm?.isPresence ? "Reason optional..." : isSelf ? "Self-governance is disabled" : "Reason required..."}
                    className="h-10 w-full rounded-md border border-white/10 bg-black px-3 text-xs text-white outline-none focus:border-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-45"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <button disabled={!canGovernUser || user.realmStatus === "active"} onClick={() => props.onStatus(user, "active")} className="case-action border-emerald-300/20 bg-emerald-300/10 text-emerald-100"><CheckCircle2 size={15} /> Restore</button>
                    <button disabled={!canGovernUser || user.realmStatus === "restricted"} onClick={() => props.onStatus(user, "restricted")} className="case-action border-amber-300/20 bg-amber-300/10 text-amber-100"><Shield size={15} /> Restrict</button>
                    <button disabled={!canGovernUser || user.realmStatus === "banned"} onClick={() => props.onStatus(user, "banned")} className="case-action border-red-300/20 bg-red-400/10 text-red-100"><Ban size={15} /> Ban</button>
                    {props.rowToasts[user.id] && (
                      <span className="inline-flex h-10 items-center rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 text-xs font-black text-cyan-100">
                        {props.rowToasts[user.id]}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PermissionsPanel({
  users,
  loading,
  query,
  realm,
  refreshing,
  rowToasts,
  currentUserId,
  onQuery,
  onSearch,
  onRole,
  onProtection,
  onRefresh,
}: {
  users: RealmUser[];
  loading: boolean;
  query: string;
  realm: Realm | null;
  refreshing: boolean;
  rowToasts: Record<string, string>;
  currentUserId: string;
  onQuery: (value: string) => void;
  onSearch: () => void;
  onRole: (user: RealmUser, role: RealmUser["role"], options?: GrantOptions) => void;
  onProtection: (user: RealmUser, protectedState: boolean) => void;
  onRefresh: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, { reason: string; expiresAt: string; permissions: string[] }>>({});
  if (!hasPermission(realm, "permissions:write")) return <EmptyPanel icon={Crown} title="Access unavailable." body="Only permitted reviewers can delegate access." />;
  const roleOptions = roleOptionsFor(realm);

  function draftFor(user: RealmUser) {
    return drafts[user.id] || {
      reason: "",
      expiresAt: user.grantExpiresAt ? new Date(user.grantExpiresAt).toISOString().slice(0, 16) : "",
      permissions: (user.permissions || []).filter((permission) => grantPowerOptions.some((option) => option.value === permission)),
    };
  }

  function updateDraft(user: RealmUser, patch: Partial<{ reason: string; expiresAt: string; permissions: string[] }>) {
    setDrafts((current) => ({ ...current, [user.id]: { ...draftFor(user), ...patch } }));
  }

  function togglePermission(user: RealmUser, permission: string) {
    const draft = draftFor(user);
    const hasPower = draft.permissions.includes(permission);
    updateDraft(user, {
      permissions: hasPower
        ? draft.permissions.filter((entry) => entry !== permission)
        : [...draft.permissions, permission],
    });
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Access</h2>
          <p className="mt-1 text-sm text-neutral-500">
            Search users, appoint owners, and tune admin powers.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <form
            className="bs-sentinel-panel-search flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              onSearch();
            }}
          >
            <input
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Search people for access..."
              className="h-10 min-w-0 rounded-md border border-white/10 bg-black/45 px-3 text-sm text-white outline-none focus:border-cyan-300/45"
            />
            <button className="inline-flex h-10 items-center gap-2 rounded-md bg-cyan-200 px-4 text-sm font-black text-black">
              <Search size={15} /> Search
            </button>
          </form>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-white/10 bg-white/[0.05] px-3 text-sm font-bold text-neutral-200 transition hover:border-cyan-300/30 hover:bg-cyan-300/10 disabled:cursor-wait disabled:opacity-60"
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin text-cyan-200" : "text-cyan-200"} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>
      {loading ? <LoadingPanel label="Reading access..." /> : users.length === 0 ? <EmptyPanel icon={Crown} title="No access subjects in view." body="Search a username to grant access, or refresh to view current role holders." /> : (
        <div className="grid gap-3 lg:grid-cols-2">
          {users.map((user) => {
            const isSelf = Boolean(currentUserId && user.id === currentUserId);
            const locked = Boolean(user.isPresence || user.grantLocked || (user.protected && !realm?.isPresence));
            const canChangeRole = !isSelf && !locked && (realm?.isPresence || user.role === "user" || user.role === "admin");
            const canTunePowers = Boolean(realm?.isPresence && !isSelf && !locked && user.role !== "user");
            return (
            <div key={user.id} className={`rounded-lg border p-4 ${locked ? "border-white/10 bg-white/[0.025]" : "border-cyan-300/15 bg-cyan-300/5"}`}>
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-black text-white">@{user.username}</h3>
                    </div>
                  <p className="text-xs text-neutral-500">{user.displayName || "No display name"}</p>
                </div>
                <select
                  value={user.role}
                  onChange={(event) => onRole(user, event.target.value as RealmUser["role"], { reason: draftFor(user).reason })}
                  disabled={!canChangeRole}
                  className="h-10 rounded-md border border-white/10 bg-black px-3 text-xs font-black text-white outline-none"
                >
                  {!roleOptions.includes(user.role) && <option value={user.role}>{roleLabel(user.role)}</option>}
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>{role === "user" ? "Demote" : roleLabel(role)}</option>
                  ))}
                </select>
                </div>

                {!realm?.isPresence && canChangeRole && (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      value={draftFor(user).reason}
                      onChange={(event) => updateDraft(user, { reason: event.target.value })}
                      placeholder="Reason required..."
                      className="h-10 min-w-0 flex-1 rounded-md border border-white/10 bg-black px-3 text-xs text-white outline-none focus:border-cyan-300/45"
                    />
                    {rowToasts[user.id] && (
                      <span className="inline-flex h-10 items-center rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 text-xs font-black text-cyan-100">
                        {rowToasts[user.id]}
                      </span>
                    )}
                  </div>
                )}

                {locked && (
                  <div className="rounded-md border border-white/10 bg-black/35 px-3 py-2 text-xs font-bold text-neutral-400">
                    {user.isPresence ? "This identity cannot be edited here." : user.grantLockedReason || "This access is protected."}
                  </div>
                )}

                {rowToasts[user.id] && !canTunePowers && (realm?.isPresence || !canChangeRole) && (
                  <span className="inline-flex h-10 items-center self-start rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 text-xs font-black text-cyan-100">
                    {rowToasts[user.id]}
                  </span>
                )}

                {canTunePowers && (
                  <div className="rounded-md border border-white/10 bg-black/35 p-3">
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_180px_auto]">
                      <input
                        value={draftFor(user).reason}
                        onChange={(event) => updateDraft(user, { reason: event.target.value })}
                        placeholder="Reason or private note..."
                        className="h-10 rounded-md border border-white/10 bg-black px-3 text-xs text-white outline-none focus:border-cyan-300/45"
                      />
                      <input
                        type="datetime-local"
                        value={draftFor(user).expiresAt}
                        onChange={(event) => updateDraft(user, { expiresAt: event.target.value })}
                        className="h-10 rounded-md border border-white/10 bg-black px-3 text-xs text-white outline-none focus:border-cyan-300/45"
                      />
                      <button
                        onClick={() => onProtection(user, !user.protected)}
                        disabled={Boolean(user.grantLocked)}
                        className={`inline-flex h-10 items-center justify-center gap-2 rounded-md border px-3 text-xs font-black transition ${
                          user.protected ? "border-emerald-300/20 bg-emerald-300/10 text-emerald-100" : "border-white/10 bg-white/[0.04] text-neutral-300"
                        }`}
                      >
                        <ShieldCheck size={14} />
                        {user.protected ? "Protected" : "Protect"}
                      </button>
                    </div>
                    {realm?.isPresence && <div className="mt-3 flex flex-wrap gap-2">
                      {grantPowerOptions.map((option) => {
                        const active = draftFor(user).permissions.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            onClick={() => togglePermission(user, option.value)}
                            className={`rounded-md border px-2.5 py-1.5 text-[11px] font-black transition ${
                              active
                                ? "border-cyan-300/25 bg-cyan-300/10 text-cyan-100"
                                : "border-white/10 bg-white/[0.03] text-neutral-500 hover:text-neutral-200"
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => {
                          const draft = draftFor(user);
                          onRole(user, user.role, {
                            reason: draft.reason,
                            permissions: draft.permissions,
                            expiresAt: draft.expiresAt || null,
                            protected: user.protected,
                          });
                        }}
                        className="inline-flex h-10 items-center gap-2 rounded-md bg-cyan-200 px-3 text-xs font-black text-black transition hover:bg-white"
                      >
                        <CheckCircle2 size={14} />
                        Apply powers
                      </button>
                      {rowToasts[user.id] && (
                        <span className="inline-flex h-10 items-center rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 text-xs font-black text-cyan-100">
                          {rowToasts[user.id]}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function OrdersPanel(props: { orders: RealmOrder[]; loading: boolean; realm: Realm | null; note: string; onNote: (value: string) => void; onCreate: () => void }) {
  const canWrite = hasPermission(props.realm, "orders:write");
  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-black text-white">Orders and interventions</h2>
        <p className="mt-1 text-sm text-neutral-500">Role changes, safety actions, and deliberate intervention notes.</p>
      </div>
      {canWrite && (
        <div className="mb-4 grid gap-2 rounded-lg border border-white/10 bg-black/35 p-4 lg:grid-cols-[minmax(0,1fr)_auto]">
          <textarea value={props.note} onChange={(event) => props.onNote(event.target.value.slice(0, 1000))} placeholder="Record an intervention note..." rows={2} className="resize-none rounded-md border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-cyan-300/45" />
          <button onClick={props.onCreate} disabled={!props.note.trim()} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-cyan-200 px-4 text-sm font-black text-black disabled:opacity-50"><ScrollText size={15} /> Record</button>
        </div>
      )}
      {props.loading ? <LoadingPanel label="Reading action log..." /> : props.orders.length === 0 ? <EmptyPanel icon={ScrollText} title="No visible actions yet." body="Record an intervention note or let reviewer actions appear here." /> : (
        <div className="space-y-2">
          {props.orders.map((order) => (
            <div key={order.id} className="rounded-lg border border-white/10 bg-black/35 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-black text-white">@{order.actorUsername || "system"}</span>
                  <span className="rounded-md border border-cyan-300/20 bg-cyan-300/10 px-2 py-0.5 text-xs font-black text-cyan-100">{roleLabel(order.actorRole)}</span>
                  <span className="text-neutral-500">{order.action}</span>
                  {order.targetUsername && <span className="text-neutral-300">@{order.targetUsername}</span>}
                </div>
                <span className="text-xs text-neutral-600">{formatDate(order.createdAt)}</span>
              </div>
              {order.details?.reason && <p className="mt-2 text-xs text-neutral-400">Reason: {order.details.reason}</p>}
              {order.details?.note && <p className="mt-2 text-xs text-neutral-400">{order.details.note}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportsPanel(props: {
  reports: RealmReport[];
  summary: ReportsSummary | null;
  loading: boolean;
  status: string;
  query: string;
  realm: Realm | null;
  onStatus: (value: string) => void;
  onQuery: (value: string) => void;
  onSearch: () => void;
  onUpdate: (report: RealmReport, status: RealmReport["status"]) => void;
}) {
  const canWrite = hasPermission(props.realm, "reports:write");
  const statusOptions = ["all", "open", "in_review", "escalated", "resolved", "dismissed"];
  const openCount = props.summary?.byStatus
    ?.filter((row) => ["open", "in_review", "escalated"].includes(row.status))
    .reduce((sum, row) => sum + row.count, 0) || 0;
  const highCount = props.summary?.bySeverity?.find((row) => row.severity === "high")?.count || 0;

  return (
    <div className="pb-8">
      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="text-xl font-black text-white">Reports</h2>
          <p className="mt-1 text-sm text-neutral-500">Space reports collected into one review queue.</p>
        </div>
        <form
          className="bs-sentinel-panel-search flex flex-col gap-2 sm:flex-row"
          onSubmit={(event) => {
            event.preventDefault();
            props.onSearch();
          }}
        >
          <label className="relative min-w-0 sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={props.query}
              onChange={(event) => props.onQuery(event.target.value)}
              placeholder="Search reports, spaces, targets..."
              className="h-10 w-full rounded-md border border-white/10 bg-black/45 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-300/45"
            />
          </label>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-cyan-200 px-4 text-sm font-black text-black transition hover:bg-white">
            <Search size={15} />
            Search
          </button>
        </form>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-white/10 bg-black/35 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">In view</p>
          <p className="mt-2 text-2xl font-black text-white">{props.reports.length}</p>
        </div>
        <div className="rounded-lg border border-amber-300/15 bg-amber-300/5 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-amber-200/70">Open pressure</p>
          <p className="mt-2 text-2xl font-black text-amber-100">{openCount}</p>
        </div>
        <div className="rounded-lg border border-red-300/15 bg-red-400/5 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-red-200/70">High severity</p>
          <p className="mt-2 text-2xl font-black text-red-100">{highCount}</p>
        </div>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
        {statusOptions.map((option) => (
          <button
            key={option}
            onClick={() => props.onStatus(option)}
            className={`shrink-0 rounded-md border px-3 py-2 text-xs font-black transition ${
              props.status === option
                ? "border-cyan-300/30 bg-cyan-300/10 text-white"
                : "border-white/10 bg-black/30 text-neutral-400 hover:text-white"
            }`}
          >
            {option === "all" ? "All" : reportStatusLabel(option)}
          </button>
        ))}
      </div>

      {props.loading ? (
        <LoadingPanel label="Reading reports..." />
      ) : props.reports.length === 0 ? (
        <EmptyPanel icon={Flag} title="No reports here." body="Change the filter or wait for a space report to arrive." />
      ) : (
        <div className="space-y-3 pb-8">
          {props.reports.map((report) => (
            <article key={report.id} className="rounded-lg border border-white/10 bg-black/35 p-4 transition hover:border-cyan-300/25">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-md border px-2.5 py-1 text-xs font-black ${reportStatusTone(report.status)}`}>
                      {reportStatusLabel(report.status)}
                    </span>
                    <span className="rounded-md border border-cyan-300/20 bg-cyan-300/10 px-2.5 py-1 text-xs font-black text-cyan-100">
                      {report.spaceTitle}
                    </span>
                    <span className={`rounded-md border px-2.5 py-1 text-xs font-black ${
                      report.severity === "high"
                        ? "border-red-300/25 bg-red-400/10 text-red-100"
                        : report.severity === "low"
                          ? "border-neutral-600 bg-neutral-800/50 text-neutral-300"
                          : "border-amber-300/25 bg-amber-300/10 text-amber-100"
                    }`}>
                      {report.severity}
                    </span>
                  </div>
                  <h3 className="mt-3 text-sm font-black text-white">{report.reason}</h3>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-neutral-300">
                    {report.details || "No extra details."}
                  </p>
                  <p className="mt-2 text-xs text-neutral-600">
                    @{report.reporterUsername} reported {report.targetType} - {report.targetId}
                  </p>
                </div>
                <div className="shrink-0 text-left text-xs leading-6 text-neutral-500 lg:text-right">
                  <p>Created {formatDate(report.createdAt)}</p>
                  <p>Handled {formatDate(report.handledAt)}</p>
                  {report.handledByRole && <p className="text-cyan-200">{roleLabel(report.handledByRole)}</p>}
                </div>
              </div>

              {canWrite && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-3">
                  {(["in_review", "escalated", "resolved", "dismissed"] as RealmReport["status"][]).map((status) => (
                    <button
                      key={status}
                      disabled={report.status === status}
                      onClick={() => props.onUpdate(report, status)}
                      className="case-action border-white/10 bg-white/[0.04] text-neutral-300 hover:border-cyan-300/30 hover:bg-cyan-300/10 hover:text-white"
                    >
                      {status === "resolved" ? <CheckCircle2 size={15} /> : status === "dismissed" ? <XCircle size={15} /> : status === "escalated" ? <Flag size={15} /> : <Clock3 size={15} />}
                      {reportStatusLabel(status)}
                    </button>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingPanel({ label }: { label: string }) {
  return (
    <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-white/10 bg-black/35 text-neutral-400">
      <RefreshCw size={18} className="mr-3 animate-spin text-cyan-200" />
      {label}
    </div>
  );
}

function EmptyPanel({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-white/10 bg-black/35 text-center">
      <Icon size={34} className="mb-3 text-neutral-600" />
      <h2 className="text-lg font-black text-white">{title}</h2>
      <p className="mt-1 text-sm text-neutral-500">{body}</p>
    </div>
  );
}


